import dayjs, {
  Dayjs,
  ManipulateType,
  OpUnitType,
  QUnitType,
  UnitType,
} from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import isoWeeksInYear from "dayjs/plugin/isoWeeksInYear";
import isTodayPlugin from "dayjs/plugin/isToday";
import relativeTime from "dayjs/plugin/relativeTime";
import timeZonePlugin from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import utcPlugin from "dayjs/plugin/utc";
import weekdayPlugin from "dayjs/plugin/weekday";
import { isString } from "lodash";

import {
  DATE_FORMAT_WITH_TIME,
  DATE_FORMAT_YMD,
  DATE_FORMAT_YMD_WITH_TIME,
  FIRST_HOUR_OF_DAY,
  HOURS_IN_DAY,
  INDEX_NOT_FOUND,
  ISO_DATE_LENGTH,
  ISO_DATE_TIME_UTC_PATTERN,
  LAST_HOUR_OF_DAY,
  MAIN_DATE_FORMAT,
  MAIN_DATETIME_DELIMITER,
  MAIN_TIME_FORMAT,
  MAIN_TIME_FORMAT_SEC,
  MAIN_TIME_FORMAT_SEC_MS,
  MIN_DATE_VALUE,
  NO_VALUE_DISPLAY,
  REFERENCE_DATE_YMD,
  relativeTimeEn,
  relativeTimeHe,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
  ZERO_TIME_HHmm,
} from "../constants";
import { TimeZoneEnum } from "../enums";
import {
  DateDayjsCompatible,
  DatesUnit,
  DateYMDString,
  DefaultTime,
  IFormAttributes,
  TTimeHHmm,
} from "../models";
import { Log } from "./log.helpers";
import { isNullOrEmptySpace } from "./string.helpers";

const timeFormats = [
  MAIN_TIME_FORMAT, // 24-hour format, e.g. 14:30
  "H:mm", // 24-hour format without leading zero, e.g. 9:30
  "hh:mm A", // 12-hour format with leading zero and uppercase AM/PM, e.g. 02:30 PM
  "h:mm A", // 12-hour format without leading zero and uppercase AM/PM, e.g. 2:30 PM
  "hh:mm a", // 12-hour format with leading zero and lowercase am/pm, e.g. 02:30 pm
  "h:mm a", // 12-hour format without leading zero and lowercase am/pm, e.g. 2:30 pm
];

declare module "dayjs" {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Dayjs {
    toISOString(): TDateISO;
  }
}

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(weekdayPlugin);
dayjs.extend(utcPlugin);
dayjs.extend(isTodayPlugin);
dayjs.extend(updateLocale);
dayjs.extend(relativeTime);
dayjs.extend(timeZonePlugin);
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.updateLocale("en", {
  relativeTime: relativeTimeEn,
});
dayjs.updateLocale("he", {
  relativeTime: relativeTimeHe,
});

// eslint-disable-next-line @typescript-eslint/naming-convention
export const TIME_STRING_LENGTH_Hmm = 4;
// eslint-disable-next-line @typescript-eslint/naming-convention
export const TIME_STRING_LENGTH_HHmm = 5;

const formattedDateCalendarMap: Map<TDateISO, DateYMDString> = new Map();

export class DateHelpers {
  static isBefore(
    date1: DateDayjsCompatible,
    date2: DateDayjsCompatible,
    timezone: TimeZoneEnum,
    unit: UnitType = "day",
  ): boolean {
    if (!isValidDateTime(date1) || !isValidDateTime(date2)) {
      return false;
    }

    return dateUTCApplyTimezone(date1, timezone).isBefore(
      dateUTCApplyTimezone(date2, timezone),
      unit,
    );
  }

  static isWithinDays(
    date1: TDateISO,
    date2: TDateISO,
    days: number,
    timezone: TimeZoneEnum,
    before = true,
  ): boolean {
    if (!isValidDateTime(date1) || !isValidDateTime(date2)) {
      return false;
    }

    const method = before ? "add" : "subtract";
    const dateToCheck = dayjs(date1)[method](days, "days").toISOString();

    return this.isBefore(date2, dateToCheck, timezone);
  }

  static differenceBetweenDates(
    date1ISO: TDateISO,
    date2ISO?: TDateISO,
    float = false,
    unit: "day" | "month" = "day",
  ): number | null {
    if (!date1ISO || !dayjs(date1ISO).isValid()) {
      return null;
    }

    if (date2ISO && !dayjs(date2ISO).isValid()) {
      return null;
    }

    return dayjs(date1ISO).diff(dayjs(date2ISO || undefined), unit, float);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidDateTime = (val: any, checkMask?: string): boolean => {
  if (!val) {
    return false;
  }

  try {
    if (!checkMask) {
      const dayjsCheckObj = dayjs(val);
      return (
        dayjsCheckObj &&
        typeof dayjsCheckObj.isValid === "function" &&
        dayjsCheckObj.isValid()
      );
    }

    const dayjsObj = dayjs(val, checkMask, true);
    return (
      dayjsObj && typeof dayjsObj.isValid === "function" && dayjsObj.isValid()
    );
  } catch (error) {
    Log.warn(
      `Error parsing date time string ${val} with format mask ${checkMask}: `,
      error,
    );
    return false;
  }
};

export const filterAcceptableTimeEditSymbols = (value: string): string => {
  if (value === "") {
    return "";
  }

  return value.replace(/[^0-9:]/g, "");
};

export const dateTimeWithoutSeconds = (dateTimeISO: TDateISO): TDateISO => {
  const dateFormatted = dayjs.utc(dateTimeISO).startOf("minute").toISOString();
  return dateFormatted;
};

export const trimSeconds = (
  dateTimeISO: TDateISO | undefined | null,
): TDateISO | undefined => {
  if (!dateTimeISO || !isValidDateTime(dateTimeISO)) {
    return undefined;
  }

  return dayjs.utc(dateTimeISO).startOf("minute").toISOString();
};

export const trimMilliseconds = (
  dateTimeISO: TDateISO | undefined | null,
): TDateISO | undefined => {
  if (!dateTimeISO || !isValidDateTime(dateTimeISO)) {
    return undefined;
  }

  return dayjs.utc(dateTimeISO).startOf("second").toISOString();
};

export const isValidTimeString = (
  time: string | null | undefined,
  timeFormatMask?: string,
): boolean => {
  if (!time) {
    return false;
  }

  try {
    const dayjsObj = dayjs(time, timeFormatMask ?? timeFormats, true);
    return (
      dayjsObj && typeof dayjsObj.isValid === "function" && dayjsObj.isValid()
    );
  } catch (error) {
    Log.warn(
      `Error parsing time string ${time} with format mask ${timeFormatMask}: `,
      error,
    );
    return false;
  }
};

export const parseTimeString = (
  value: string,
  timeFormat: string = MAIN_TIME_FORMAT,
): string => {
  return dayjs(value, timeFormat).format(timeFormat);
};

export const formatTimeISO = ({
  dateTimeISO,
  timeFormat = MAIN_TIME_FORMAT,
  timezone,
}: {
  dateTimeISO?: TDateISO | null;
  timeFormat?: string;
  timezone: TimeZoneEnum;
}): string => {
  if (!dateTimeISO || !isValidDateTime(dateTimeISO)) {
    return "";
  }

  return dateISOApplyTimezone(dateTimeISO, timezone).format(timeFormat);
};

export const formatTimeISOWithSeconds = ({
  dateTimeISO,
  timezone,
}: {
  dateTimeISO?: TDateISO | null;
  timezone: TimeZoneEnum;
}): string => {
  if (!dateTimeISO || !isValidDateTime(dateTimeISO)) {
    return "";
  }

  return dateISOApplyTimezone(dateTimeISO, timezone).format("HH:mm:ss");
};

export const formatTimeHHmm = (
  dateTimeISO: TDateISO | null,
  timezone: TimeZoneEnum,
): TTimeHHmm | "" => {
  return formatTimeISO({
    dateTimeISO,
    timezone,
    timeFormat: MAIN_TIME_FORMAT,
  }) as TTimeHHmm;
};

export const formatDayjsToTimeHHmm = (
  date: Dayjs | null | undefined,
  timezone: TimeZoneEnum,
): TTimeHHmm => {
  if (!date) {
    Log.warn("Empty date value supplied to formatting", date, timezone);
    return ZERO_TIME_HHmm;
  }

  return dayjs(date).tz(timezone).format(MAIN_TIME_FORMAT) as TTimeHHmm;
};

export const formatHHmmTime = (
  hhmmValue: TTimeHHmm,
  timeFormat: string = MAIN_TIME_FORMAT,
  timezone: TimeZoneEnum,
): string => {
  if (!hhmmValue || !isString(hhmmValue)) {
    return "";
  }

  const date = isValidTimeString(hhmmValue)
    ? getDayjsFromTimeString(hhmmValue, timezone)
    : dateUTCApplyTimezone(hhmmValue, timezone);

  return date.format(timeFormat);
};

export const getDayjsFromTimeString = (
  timeString: string,
  timezone: TimeZoneEnum,
): dayjs.Dayjs => {
  try {
    const parsedTime = dayjs(timeString, timeFormats).tz(timezone, true);
    return parsedTime?.isValid() ? parsedTime : dayjs();
  } catch (error) {
    Log.warn(
      `Error parsing time string ${timeString} with format mask ${timeFormats}: `,
      error,
    );
    return dayjs();
  }
};

export const getFormattedTimeFromTimeString = (
  value: string,
  timeFormat = MAIN_TIME_FORMAT,
): string => {
  if (isNullOrEmptySpace(value)) {
    return value;
  }

  for (const format of timeFormats) {
    try {
      const parsedTime = dayjs(value, format, true);

      if (
        parsedTime &&
        typeof parsedTime.isValid === "function" &&
        parsedTime.isValid()
      ) {
        return parsedTime.format(timeFormat);
      }
    } catch (_error) {
      /* Continue to next format */
    }
  }

  return value;
};

/**
 * Reformat a date-only bucket for display, without moving it.
 *
 * A `YYYY-MM-DD` produced by `date_trunc('day', ...)` is a label, not an instant — the grouping was
 * already decided server-side, in the database's own reckoning of a day. Every other formatter here
 * applies a timezone, which for a bare date means parsing it as UTC midnight and then converting:
 * `2026-08-19` renders as `18.08.2026` for any tenant behind UTC, and the chart then disagrees with
 * the data it is drawn from.
 *
 * This one only changes the notation. A bare `YYYY-MM-DD` parses as local midnight and is formatted
 * straight back out, so the day is the same in every timezone.
 *
 * Typed `TDateISODate` rather than `string`: the parse is strict on `YYYY-MM-DD`, so anything else
 * falls through to the raw value and the caller silently gets an unformatted axis. The type is the
 * cheapest place to catch that.
 */
export const formatDayBucket = (
  day: TDateISODate | null | undefined,
  dateFormat: string = MAIN_DATE_FORMAT,
): string => {
  if (!day || !dayjs(day, DATE_FORMAT_YMD, true).isValid()) {
    return day ?? "";
  }

  return dayjs(day, DATE_FORMAT_YMD, true).format(dateFormat);
};

export const formatDate = ({
  dateISO,
  dateFormat = MAIN_DATE_FORMAT,
  timezone,
}: {
  dateISO: TDateISO;
  dateFormat?: string;
  timezone: TimeZoneEnum;
}): string => {
  if (!isValidDateTime(dateISO)) {
    return "";
  }

  return dateISOApplyTimezone(dateISO, timezone).format(dateFormat);
};

export const formatDateDMY = (
  dateISO: TDateISO,
  timezone: TimeZoneEnum,
): string => {
  return formatDate({ dateISO, timezone, dateFormat: MAIN_DATE_FORMAT });
};

export const formatDateAtTimezone = (
  dateUTC: DateDayjsCompatible,
  timezone: TimeZoneEnum,
  dateFormat = MAIN_DATE_FORMAT,
): string => {
  if (!isValidDateTime(dateUTC)) {
    return "";
  }

  return dayjs(dateUTC, timezone).format(dateFormat);
};

interface IFormatDateTimeArguments {
  dateTimeISO: TDateISO | null | undefined;
  timeFormat?: string;
  dateFormat?: string;
  delimiter?: string;
  timezone: TimeZoneEnum;
}

export const formatDatetime = ({
  dateTimeISO,
  timeFormat,
  dateFormat,
  delimiter = MAIN_DATETIME_DELIMITER,
  timezone,
}: IFormatDateTimeArguments): string => {
  if (!dateTimeISO || !isValidDateTime(dateTimeISO)) {
    return "";
  }

  const dateTimeIsoNarrowed: TDateISO = dateTimeISO as TDateISO;

  return `${formatDate({
    dateISO: dateTimeIsoNarrowed,
    dateFormat,
    timezone,
  })}${delimiter}${formatTimeISO({
    dateTimeISO: dateTimeIsoNarrowed,
    timeFormat,
    timezone,
  })}`;
};

export const datePartWithoutTimeYYYYMMDD = (
  dateISO: TDateISO,
  timezone: TimeZoneEnum,
): DateYMDString => {
  return dateISOApplyTimezone(dateISO, timezone)
    .startOf("day")
    .format(DATE_FORMAT_YMD) as DateYMDString;
};

export const isTodayDate = (
  dateISO: TDateISO | undefined | null,
  timezone: TimeZoneEnum,
): boolean => {
  if (!dateISO || !isValidDateTime(dateISO)) {
    return false;
  }

  return dateISOApplyTimezone(dateISO, timezone).isToday();
};

export const generateDatesArrayYMDString = (
  dateFromISO: TDateISO,
  dateToISO: TDateISO,
  timezone: TimeZoneEnum,
): DateYMDString[] => {
  const dt1 = dayjs.utc(dateFromISO).tz(timezone).startOf("day");
  const dt2 = dayjs.utc(dateToISO).tz(timezone).startOf("day");
  const days = dt2.diff(dt1, "days");
  const dates: DateYMDString[] = [];

  if (days === 0) {
    return [dt1.format(DATE_FORMAT_YMD) as DateYMDString];
  }

  for (let i = 0; i <= days; i++) {
    const dateYMD = dt1.add(i, "day").format(DATE_FORMAT_YMD) as DateYMDString;
    dates.push(dateYMD);
  }

  return dates;
};

export const addDays = (
  dateUTC: DateDayjsCompatible,
  days: number,
): TDateISO => {
  return dayjs.utc(dateUTC).add(days, "day").utc().toISOString();
};

export const addMinutes = (
  dateUTC: DateDayjsCompatible,
  minutes: number,
): TDateISO => {
  return dayjs.utc(dateUTC).add(minutes, "minute").utc().toISOString();
};

export const calculateDateDifference = (
  date1ISO: TDateISO,
  date2ISO: TDateISO,
  unit: QUnitType | OpUnitType = "day",
  signed = false,
): number => {
  if (!isValidDateTime(date1ISO) || !isValidDateTime(date2ISO)) {
    return 0;
  }

  const date1 = dayjs.utc(date1ISO);
  const date2 = dayjs.utc(date2ISO);

  return signed ? date2.diff(date1, unit) : Math.abs(date2.diff(date1, unit));
};

export const timeDiffMinutesCeiling = (
  date1ISO: TDateISO,
  date2ISO: TDateISO,
): number => {
  if (!isValidDateTime(date1ISO) || !isValidDateTime(date2ISO)) {
    return 0;
  }

  const diffInSeconds = Math.abs(
    dayjs.utc(date1ISO).diff(dayjs.utc(date2ISO), "second"),
  );
  return Math.ceil(diffInSeconds / SECONDS_IN_MINUTE);
};

export const timeDiffSeconds = (
  date1ISO: TDateISO | undefined,
  date2ISO: TDateISO | undefined,
): number => {
  if (!isValidDateTime(date1ISO) || !isValidDateTime(date2ISO)) {
    return 0;
  }

  const diffInSeconds = Math.abs(
    dayjs.utc(date1ISO).diff(dayjs.utc(date2ISO), "second"),
  );
  return diffInSeconds;
};

// Any fixed date works: in UTC there is no DST, so every day is exactly 24h.
const WALL_CLOCK_REFERENCE_DATE = "2000-01-01";

/** Parses an HH:mm wall-clock time onto a fixed UTC date, so time arithmetic never depends on DST. */
const parseWallClockTime = (time: string): dayjs.Dayjs | null => {
  for (const format of timeFormats) {
    try {
      const parsed = dayjs.utc(
        `${WALL_CLOCK_REFERENCE_DATE} ${time}`,
        `YYYY-MM-DD ${format}`,
      );

      if (parsed && typeof parsed.isValid === "function" && parsed.isValid()) {
        return parsed;
      }
    } catch (_error) {
      /* Continue to next format */
    }
  }

  return null;
};

/**
 * Wall-clock difference between two HH:mm times; a second time earlier than the first is taken as the next day.
 * `_timezone` is ignored on purpose (kept so callers do not change): parsing onto today's date in the company
 * timezone broke on DST change days (RYDE-8473) - on 25.10.2026 legs inside 01:00-02:00 came out 24h too long
 * and legs crossing 01:00 one hour too long, which corrupted saved route paths.
 */
export const timeDifferenceSameDate = (
  time1string: string,
  time2string: string,
  unit: UnitType | undefined = "minute",
  _timezone?: TimeZoneEnum,
): number | undefined => {
  if (
    !time1string ||
    !time2string ||
    !isValidTimeString(time1string) ||
    !isValidTimeString(time2string)
  ) {
    return undefined;
  }

  const parsedTime1 = parseWallClockTime(time1string);
  const parsedTime2 = parseWallClockTime(time2string);

  if (!parsedTime1 || !parsedTime2) {
    return undefined;
  }

  const adjustedTime2 = parsedTime2.isBefore(parsedTime1)
    ? parsedTime2.add(1, "day")
    : parsedTime2;

  return adjustedTime2.diff(parsedTime1, unit);
};

export const timeDiffMinutesSigned = (
  date1any: TDateISO,
  date2any: TDateISO,
): number => {
  return Math.ceil(
    dayjs.utc(date2any).diff(dayjs.utc(date1any), "second") / SECONDS_IN_MINUTE,
  );
};

export const equalDates = (date1: TDateISO, date2: TDateISO): boolean => {
  if (!isValidDateTime(date1) || !isValidDateTime(date2)) {
    return false;
  }

  return dayjs.utc(date1).isSame(dayjs.utc(date2), "day");
};

export const equalDatesCalendar = (
  dateUTC: TDateISO,
  dateYYYYMMDD: DateYMDString,
  timezone: string,
): boolean => {
  const utcDateFormatted = dayjs
    .utc(dateUTC)
    .tz(timezone)
    .format(DATE_FORMAT_YMD);
  return utcDateFormatted === dateYYYYMMDD;
};

export const getDateCalendar = (
  bookTime: TDateISO,
  timeZone: string,
): DateYMDString => {
  let formattedDate = formattedDateCalendarMap.get(bookTime);

  if (!formattedDate) {
    formattedDate = dayjs
      .utc(bookTime)
      .tz(timeZone)
      .format(DATE_FORMAT_YMD) as DateYMDString;
    formattedDateCalendarMap.set(bookTime, formattedDate);
  }

  return formattedDate;
};

export const isISODateStr = (str: string): str is TDateISO => {
  if (!ISO_DATE_TIME_UTC_PATTERN.test(str)) {
    return false;
  }

  const dateInstance = new Date(str);

  if (isNaN(dateInstance.getTime())) {
    return false;
  }

  // Date.parse rolls an out-of-range day over instead of rejecting it ("2026-02-31" becomes 2026-03-03), so require the
  // calendar day to survive the round trip. Unambiguous because the pattern above only admits UTC ("...Z") instants.
  return (
    dateInstance.toISOString().slice(0, ISO_DATE_LENGTH) ===
    str.slice(0, ISO_DATE_LENGTH)
  );
};

export const getDefaultDatetimeUTCValue = (
  defaultTime: IFormAttributes["defaultTime"],
  timezone: TimeZoneEnum,
): Dayjs => {
  switch (true) {
    case defaultTime === DefaultTime.DAY_START:
      return dayjs().tz(timezone).startOf("day").utc();
    case defaultTime === DefaultTime.DAY_END:
      return dayjs().tz(timezone).endOf("day").utc();
    default:
      return dayjs().tz(timezone).utc();
  }
};

export const dateToComparableNumber = (
  date: TDateISO | null | undefined,
): number => {
  if (!date) {
    return 0;
  }

  return dayjs.utc(date).valueOf();
};

export const parseTimeAtReferenceDate = ({
  timeHHmm,
  timezone,
  referenceDateYMD = REFERENCE_DATE_YMD,
}: {
  timeHHmm: string | null | undefined;
  timezone: TimeZoneEnum;
  referenceDateYMD?: DateYMDString;
}): Dayjs | null => {
  if (!timeHHmm) {
    return null;
  }

  try {
    const parsed = dayjs.tz(
      `${referenceDateYMD} ${timeHHmm}`,
      "YYYY-MM-DD HH:mm",
      timezone,
    );

    return parsed.isValid() ? parsed : null;
  } catch (error) {
    Log.debug(
      `Failed to parse reference time '${timeHHmm}' at timezone '${timezone}'`,
      error,
    );
    return null;
  }
};

export const currentDateAtMidnightISO = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).startOf("day").utc().toISOString();

export const isBeforeTimeString = (
  from: TTimeHHmm | undefined,
  to: TTimeHHmm | undefined,
): boolean => {
  const fromTime = dayjs(from, MAIN_TIME_FORMAT);
  const toTime = dayjs(to, MAIN_TIME_FORMAT);

  return fromTime?.isBefore(toTime);
};

/** "06:50–07:10", or a single value when from/to match ("06:50"). */
export const formatTimeRangeLabel = ({
  from,
  to,
  separator = "–",
}: {
  from: string | null | undefined;
  to: string | null | undefined;
  separator?: string;
}): string => {
  const fromTrimmed = from?.trim() || "";
  const toTrimmed = to?.trim() || "";

  if (fromTrimmed && toTrimmed) {
    return fromTrimmed === toTrimmed
      ? fromTrimmed
      : `${fromTrimmed}${separator}${toTrimmed}`;
  }

  return fromTrimmed || toTrimmed;
};

export const formatDateYMD = (
  dateISO: TDateISO,
  timezone: TimeZoneEnum,
): string => {
  if (!isValidDateTime(dateISO)) {
    return dateISOApplyTimezone(MIN_DATE_VALUE, timezone).format(
      DATE_FORMAT_YMD,
    );
  }

  return dateISOApplyTimezone(dateISO, timezone).format(DATE_FORMAT_YMD);
};

export const formatFromNowTime = (
  dateISO: TDateISO,
  timezone: TimeZoneEnum,
): string => {
  return dateISOApplyTimezone(dateISO, timezone).fromNow();
};

export const getNextDayStartDateTime = (
  timezone: TimeZoneEnum,
  dateISOIfNotToday?: TDateISO,
): TDateISO => {
  const dt = dateISOIfNotToday
    ? dateISOApplyTimezone(dateISOIfNotToday, timezone)
    : nowInTimezone(timezone);
  return dt.add(1, "day").startOf("day").utc().toISOString();
};

export const getNextDayEndDateTime = (
  timezone: TimeZoneEnum,
  dateISOIfNotToday?: TDateISO,
): TDateISO => {
  const dt = dateISOIfNotToday
    ? dateISOApplyTimezone(dateISOIfNotToday, timezone)
    : nowInTimezone(timezone);
  return dt.add(1, "day").endOf("day").utc().toISOString();
};

export interface IISOWeekAndYear {
  week: number;
  year: number;
}

export const getISOWeekAndYear = (
  date: TDateISO,
  timezone: TimeZoneEnum,
): IISOWeekAndYear => {
  const parsedDate = dateISOApplyTimezone(date, timezone);
  const week = parsedDate.isoWeek();
  const year = parsedDate.isoWeekYear();

  const weekAndYear: IISOWeekAndYear = { week, year };
  return weekAndYear;
};

export const isMidnight = (
  isoStringUTC: TDateISO | undefined,
  timezone: TimeZoneEnum,
): boolean => {
  if (!isoStringUTC) {
    return false;
  }

  return startOfZonedCalendarDay({
    dateISO: isoStringUTC,
    timezone,
  }).isSame(dateISOApplyTimezone(isoStringUTC, timezone));
};

export const utcDateWithoutTimePartToISO = (
  dateStr: DateDayjsCompatible,
  timezone: TimeZoneEnum,
): TDateISO => {
  const dateInTimezone = dayjs.utc(dateStr).tz(timezone).startOf("day").utc();

  return dateInTimezone.toISOString();
};

export const timezoneAdjustedDateToISO = (
  date: DateDayjsCompatible,
  timezone: TimeZoneEnum,
): TDateISO => dayjs.tz(date, timezone).utc().toISOString();

export const addDaysPreservingWallClock = ({
  date,
  days,
  timezone,
}: {
  date: DateDayjsCompatible;
  days: number;
  timezone: TimeZoneEnum;
}): Date | undefined => {
  if (!isValidDateTime(date)) {
    return undefined;
  }

  const original = dayjs(date).tz(timezone);
  const targetDate = original.add(days, "day").format(DATE_FORMAT_YMD);
  const wallTime = original.format(MAIN_TIME_FORMAT_SEC);

  return dayjs.tz(`${targetDate} ${wallTime}`, timezone).toDate();
};

export const timezoneAdjustedDateToDayStartISO = (
  date: DateDayjsCompatible,
  timezone: TimeZoneEnum,
): TDateISO => dayjs.tz(date, timezone).startOf("day").utc().toISOString();

export const nowInTimezone = (timezone: TimeZoneEnum): dayjs.Dayjs =>
  dayjs().tz(timezone);

// Day-granularity comparison in the company timezone, not the viewer's. Takes a UTC ISO instant so the caller cannot
// pass a local-time value by mistake; startOf("day") also drops any seconds MUI may have appended.
export const isDateBeforeToday = (
  dateISO: TDateISO,
  timezone: TimeZoneEnum,
): boolean => {
  if (!isISODateStr(dateISO)) {
    return false;
  }

  return dateISOApplyTimezone(dateISO, timezone)
    .startOf("day")
    .isBefore(nowInTimezone(timezone).startOf("day"));
};

export const nowTDateISO = (
  timezone: TimeZoneEnum,
  roundSeconds = true,
): TDateISO => {
  if (roundSeconds) {
    return nowInTimezone(timezone).second(0).millisecond(0).toISOString();
  }

  return nowInTimezone(timezone).toISOString();
};

export const dateISOApplyTimezone = (
  dateISO: TDateISO,
  timezone: TimeZoneEnum,
): dayjs.Dayjs => dayjs.utc(dateISO).tz(timezone);

const START_OF_CALENDAR_DAY_LOCAL = "00:00:00.000";
const END_OF_CALENDAR_DAY_LOCAL = "23:59:59.999";
const DATE_FORMAT_YMD_WITH_TIME_MS = `${DATE_FORMAT_YMD} ${MAIN_TIME_FORMAT_SEC_MS}`;

const parseZonedWallClock = ({
  calendarDay,
  localTime,
  timezone,
}: {
  calendarDay: string;
  localTime: string;
  timezone: TimeZoneEnum;
}): Dayjs =>
  dayjs.tz(
    `${calendarDay} ${localTime}`,
    DATE_FORMAT_YMD_WITH_TIME_MS,
    timezone,
  );

// Date-only `dayjs.tz("YYYY-MM-DD", zone)` goes through the host Date parser. After Israel
// fall-back the browser is already on IST, so that becomes winter midnight (01:00 IDT) instead
// of the IDT 00:00 that started the civil day. Always parse an explicit wall-clock time.
export const startOfZonedCalendarDay = ({
  dateISO,
  timezone,
}: {
  dateISO: TDateISO;
  timezone: TimeZoneEnum;
}): Dayjs => {
  const calendarDay = dateISOApplyTimezone(dateISO, timezone).format(
    DATE_FORMAT_YMD,
  );
  return parseZonedWallClock({
    calendarDay,
    localTime: START_OF_CALENDAR_DAY_LOCAL,
    timezone,
  });
};

export const shiftZonedCalendarDate = ({
  dateISO,
  timezone,
  amount,
  unit,
}: {
  dateISO: TDateISO;
  timezone: TimeZoneEnum;
  amount: number;
  unit: ManipulateType;
}): TDateISO => {
  const calendarDay = formatDateYMD(dateISO, timezone);
  const shiftedYmd = dayjs(calendarDay, DATE_FORMAT_YMD)
    .add(amount, unit)
    .format(DATE_FORMAT_YMD);

  return parseZonedWallClock({
    calendarDay: shiftedYmd,
    localTime: START_OF_CALENDAR_DAY_LOCAL,
    timezone,
  })
    .utc()
    .toISOString();
};

export const endOfZonedCalendarDay = ({
  dateISO,
  timezone,
}: {
  dateISO: TDateISO;
  timezone: TimeZoneEnum;
}): Dayjs => {
  const calendarDay = dateISOApplyTimezone(dateISO, timezone).format(
    DATE_FORMAT_YMD,
  );
  return parseZonedWallClock({
    calendarDay,
    localTime: END_OF_CALENDAR_DAY_LOCAL,
    timezone,
  });
};

export const dateUTCApplyTimezone = (
  dateAnyUTC: DateDayjsCompatible,
  timezone: TimeZoneEnum,
): dayjs.Dayjs => dayjs.utc(dateAnyUTC).tz(timezone);

export const convertUTCToISO = (utcDate: DateDayjsCompatible): TDateISO => {
  return dayjs.utc(utcDate).toISOString();
};

export const getUTCDateOrNowInISO = (
  dateUTC: Dayjs | null | undefined,
  timezone: TimeZoneEnum,
  roundSeconds = true, //seconds need trimming, because MUI ui controls add seconds while editing date/time, which lead to unexpected result of operations +-1 min
): TDateISO => {
  if (!dateUTC) {
    return nowTDateISO(timezone, roundSeconds);
  }

  if (roundSeconds) {
    return dayjs.utc(dateUTC).second(0).millisecond(0).toISOString();
  }

  return dayjs.utc(dateUTC).toISOString();
};

export const isUtcDateBeforeToday = (
  dateUTC: DateDayjsCompatible,
  timezone: TimeZoneEnum,
): boolean =>
  dayjs(dateUTC).tz(timezone).isBefore(dayjs().tz(timezone).startOf("day"));

/**
 * True when an absolute timestamp is strictly before "now". Timezone-independent — it compares
 * instants, not wall-clock/day boundaries (unlike isUtcDateBeforeToday). Used to gate the past-ride
 * taxi-supplier UI (RYDE-7633).
 */
export const isDateTimeInPast = (date?: DateDayjsCompatible): boolean =>
  isValidDateTime(date) && dayjs(date).isBefore(dayjs());

export const mergeDateWithHHmmTime = (
  dateUTC: DateDayjsCompatible,
  timeHHmm: TTimeHHmm,
  timezone: TimeZoneEnum,
): TDateISO | undefined => {
  try {
    const parsedDate = dayjs.utc(dateUTC).tz(timezone);

    if (
      !parsedDate ||
      typeof parsedDate.isValid !== "function" ||
      !parsedDate.isValid()
    ) {
      return undefined;
    }

    const dateStartOfDay = parsedDate.startOf("day");

    if (!isValidTimeString(timeHHmm)) {
      Log.warn("Invalid time format");
      return dateStartOfDay.utc().toISOString();
    }

    const combinedDateTime = dayjs.tz(
      `${dateStartOfDay.format(MAIN_DATE_FORMAT)} ${timeHHmm}`,
      DATE_FORMAT_WITH_TIME,
      timezone,
    );

    return combinedDateTime &&
      typeof combinedDateTime.isValid === "function" &&
      combinedDateTime.isValid()
      ? combinedDateTime.utc().toISOString()
      : undefined;
  } catch (error) {
    Log.warn(
      `Error merging date ${dateUTC} with HHmm time ${timeHHmm} in timezone ${timezone}`,
      error,
    );
    return undefined;
  }
};

export const mergeTodayWithHHmmTime = (
  timeHHmm: TTimeHHmm,
  timezone: TimeZoneEnum,
): TDateISO | undefined => {
  const todayUTC = dayjs().tz(timezone).startOf("day").utc();
  return mergeDateWithHHmmTime(todayUTC, timeHHmm, timezone);
};

export const isNowInTimeStringHHmmRange = ({
  nowISO,
  startTimeHHmm,
  endTimeHHmm,
  timezone,
}: {
  nowISO: TDateISO;
  startTimeHHmm: TTimeHHmm; // format HH:mm - uniformed used for time edit save format
  endTimeHHmm: TTimeHHmm; // format HH:mm - uniformed used for time edit save format
  timezone: TimeZoneEnum;
}): boolean => {
  if (
    !isValidTimeString(startTimeHHmm, MAIN_TIME_FORMAT) ||
    !isValidTimeString(endTimeHHmm, MAIN_TIME_FORMAT)
  ) {
    return false;
  }

  const now = dayjs.utc(nowISO).tz(timezone);
  const todayMidnight = now.startOf("day");

  const start = dayjs.tz(
    `${todayMidnight.format(MAIN_DATE_FORMAT)} ${startTimeHHmm}`,
    DATE_FORMAT_WITH_TIME,
    timezone,
  );
  const end = dayjs.tz(
    `${todayMidnight.format(MAIN_DATE_FORMAT)} ${endTimeHHmm}`,
    DATE_FORMAT_WITH_TIME,
    timezone,
  );

  if (now === start || now === end) {
    return true;
  }

  // Handle overnight range
  if (end.isBefore(start)) {
    return now.isAfter(start) || now.isBefore(end);
  }

  return now.isAfter(start) && now.isBefore(end);
};

export const formatDateValidatedLocalized = (params: {
  date: DateDayjsCompatible | null | undefined;
  timezone: TimeZoneEnum;
  format?: string;
  fallbackText?: string;
}): string => {
  const {
    date,
    timezone,
    format = DATE_FORMAT_YMD_WITH_TIME,
    fallbackText = NO_VALUE_DISPLAY,
  } = params;

  try {
    const dayjsObj = date ? dayjs(date) : null;
    const isValid =
      dayjsObj && typeof dayjsObj.isValid === "function" && dayjsObj.isValid();

    if (isValid) {
      return dayjsObj?.tz(timezone)?.format(format) ?? "";
    }
  } catch (_error) {
    // Fall through to return fallbackText
  }

  return fallbackText;
};

export const addSeconds = (
  date: TDateISO | undefined,
  seconds: number,
): TDateISO | undefined => {
  if (!date) {
    return undefined;
  }

  return dayjs(date).add(seconds, "second").toISOString();
};

export const quickLogDate = (
  dt: DateDayjsCompatible | null | undefined,
  timezone = TimeZoneEnum.AsiaJerusalem,
): string => {
  if (!dt || !isValidDateTime(dt)) {
    return NO_VALUE_DISPLAY;
  }

  return dayjs.utc(dt).tz(timezone).format(DATE_FORMAT_WITH_TIME);
};

export const areDatesWithinRange = (
  date1: DateDayjsCompatible,
  date2: DateDayjsCompatible,
  range: number,
  unit: UnitType = "minutes",
): boolean => {
  return Math.abs(dayjs(date1).diff(dayjs(date2), unit)) <= range;
};

export const isDateTimeInclusiveBetween = ({
  value,
  start,
  end,
}: {
  value: DateDayjsCompatible;
  start: DateDayjsCompatible;
  end: DateDayjsCompatible;
}): boolean => {
  if (
    !isValidDateTime(value) ||
    !isValidDateTime(start) ||
    !isValidDateTime(end)
  ) {
    return false;
  }

  const timestamp = dayjs(value).valueOf();
  return (
    timestamp >= dayjs(start).valueOf() && timestamp <= dayjs(end).valueOf()
  );
};

export const addSecondsToHHmmTimeString = (
  timeBaseHHmm: TTimeHHmm,
  secondsToAdd: number,
  timezone: TimeZoneEnum,
): TTimeHHmm | "" => {
  if (!timeBaseHHmm || !isValidTimeString(timeBaseHHmm)) {
    Log.debug(`Invalid timeBaseHHmm: ${timeBaseHHmm}`);

    return "";
  }

  if (typeof secondsToAdd !== "number" || isNaN(secondsToAdd)) {
    Log.debug(`Invalid secondsToAdd: ${secondsToAdd}`);

    return "";
  }

  try {
    const dateBase = dayjs()
      .tz(timezone)
      .startOf("day")
      .format(DATE_FORMAT_YMD);
    const parsableDateWithTime = `${dateBase} ${timeBaseHHmm.padStart(TIME_STRING_LENGTH_HHmm, "0")}`;
    const dateWithTime = dayjs.tz(
      parsableDateWithTime,
      DATE_FORMAT_YMD_WITH_TIME,
      timezone,
    );

    if (
      !dateWithTime ||
      typeof dateWithTime.isValid !== "function" ||
      !dateWithTime.isValid()
    ) {
      Log.debug(`Invalid date parsed from: ${parsableDateWithTime}`);

      return "";
    }

    const updatedDate = dateWithTime.add(secondsToAdd, "second");

    return updatedDate.tz(timezone).format(MAIN_TIME_FORMAT) as TTimeHHmm;
  } catch (error) {
    Log.warn(
      `Error in addSecondsToHHmmTimeString: ${timeBaseHHmm} ${secondsToAdd} ${timezone}`,
      error,
    );

    return "";
  }
};

/**
 * Running totals of per-leg seconds (leg i connects station i -> i + 1), one entry per station:
 * index 0 is always 0 and index i is the sum of the first i legs. Negative or missing legs count as
 * zero so a timeline built from it can never run backwards.
 */
export const toCumulativeSeconds = (legSeconds: number[]): number[] => {
  const cumulativeSeconds: number[] = [0];

  legSeconds.forEach(seconds => {
    cumulativeSeconds.push(
      (cumulativeSeconds.at(-1) || 0) + Math.max(seconds || 0, 0),
    );
  });

  return cumulativeSeconds;
};

/**
 * Per-leg seconds a bulk apply should actually write: the measured value on the legs the user picked,
 * and the CURRENT inter-station delta on every other leg, so applying one leg never reverts unsaved
 * manual edits elsewhere on the route.
 * The result stops at the first unpicked leg whose current delta is unknown: there is no honest value
 * to continue the timeline with, and "unknown" must leave those stations alone rather than collapse
 * them onto the anchor.
 */
export const resolveAppliedLegSeconds = ({
  appliedLegSeconds,
  currentLegSeconds,
}: {
  appliedLegSeconds: (number | undefined)[];
  currentLegSeconds: (number | undefined)[];
}): number[] => {
  const mergedLegSeconds = currentLegSeconds.map(
    (currentSeconds, legIndex) => appliedLegSeconds[legIndex] ?? currentSeconds,
  );
  const firstUnknownIndex = mergedLegSeconds.findIndex(
    seconds => seconds === undefined,
  );
  const resolvedLegSeconds =
    firstUnknownIndex === INDEX_NOT_FOUND
      ? mergedLegSeconds
      : mergedLegSeconds.slice(0, firstUnknownIndex);

  return resolvedLegSeconds.flatMap(seconds =>
    seconds === undefined ? [] : [Math.max(seconds, 0)],
  );
};

/**
 * Turns per-leg seconds (leg i connects station i -> i + 1) into a cumulative HH:mm timeline: the
 * first entry is `startTimeHHmm` and every later one is the running total added to it.
 * Each value is computed from the anchor rather than from the previous formatted value, so neither the
 * minute grid nor a single slow leg can accumulate into a drift along a long route. An HH:mm timeline
 * cannot hold seconds, so every station lands on its NEAREST minute — rounding the running total, not
 * the legs themselves, keeps the route's total travel time within half a minute of what was applied.
 */
export const buildCumulativeTimeArrivalsFromLegSeconds = (
  legSeconds: number[],
  startTimeHHmm: TTimeHHmm = ZERO_TIME_HHmm,
): TTimeHHmm[] => {
  const anchor = isValidTimeString(startTimeHHmm)
    ? startTimeHHmm
    : ZERO_TIME_HHmm;

  return toCumulativeSeconds(legSeconds).map(cumulativeSeconds => {
    const wholeMinutesSeconds =
      Math.round(cumulativeSeconds / SECONDS_IN_MINUTE) * SECONDS_IN_MINUTE;

    return (
      addSecondsToHHmmTimeString(
        anchor,
        wholeMinutesSeconds,
        TimeZoneEnum.UTC,
      ) || anchor
    );
  });
};

const wrapHourOfDay = (hour: number): number =>
  ((hour % HOURS_IN_DAY) + HOURS_IN_DAY) % HOURS_IN_DAY;

/**
 * Local hour-of-day window around an instant. The window WRAPS around midnight instead of being clamped:
 * a 00:00 instant yields `hourFrom: 23, hourTo: 1`, which the backend reads as the inverted-therefore-wrapping
 * range 23, 0, 1. Clamping would drop the half of the comparable traffic that sits on the other side of
 * midnight.
 */
export const getHourWindowInTimezone = ({
  dateISO,
  timezone,
  hoursSpread,
}: {
  dateISO: TDateISO | undefined;
  timezone: TimeZoneEnum;
  hoursSpread: number;
}): { hourFrom: number; hourTo: number } | undefined => {
  if (!dateISO || !isValidDateTime(dateISO)) {
    return undefined;
  }

  const hour = dateISOApplyTimezone(dateISO, timezone).hour();
  const spread = Math.max(Math.floor(hoursSpread), 0);

  // A spread that already covers the whole day would wrap onto itself and read back as a narrow window.
  if (spread + spread + 1 >= HOURS_IN_DAY) {
    return { hourFrom: FIRST_HOUR_OF_DAY, hourTo: LAST_HOUR_OF_DAY };
  }

  return {
    hourFrom: wrapHourOfDay(hour - spread),
    hourTo: wrapHourOfDay(hour + spread),
  };
};

export const formattedDateFromLocalTimeAndDayOffset = ({
  time,
  offset,
  timezone,
  dateFormat = MAIN_DATE_FORMAT,
  parseTimeFormat = MAIN_TIME_FORMAT,
}: {
  time: string | undefined;
  offset: number | undefined;
  timezone: TimeZoneEnum;
  dateFormat?: string;
  parseTimeFormat?: string;
}): string => {
  if (!time || typeof offset !== "number") {
    return "";
  }

  const date = dayjs.tz(time, parseTimeFormat, timezone).add(offset, "day");

  return date.format(dateFormat);
};

export const calculateAbsDurationInSeconds = (
  currentTime: DateDayjsCompatible | undefined,
  previousTime: DateDayjsCompatible | undefined,
): number => {
  if (!isValidDateTime(currentTime) || !isValidDateTime(previousTime)) {
    return 0;
  }

  return Math.ceil(
    Math.abs(dayjs(currentTime).diff(dayjs(previousTime), "second")),
  );
};

export const parseHHmmTimeToMinutes = (timeStr: TTimeHHmm): number => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * SECONDS_IN_MINUTE + minutes; // Convert to total minutes
};

const DURATION_PART_PAD_LENGTH = 2;

/**
 * Compact duration for read-only display: "M:SS", growing to "H:MM:SS" past the hour.
 * Kept at second resolution on purpose — measured leg times are frequently under two minutes, and
 * rounding them to whole minutes hides exactly the difference the user is being asked to judge.
 */
export const formatDurationSecondsCompact = (
  durationInSeconds: number | null | undefined,
): string => {
  if (
    durationInSeconds === null ||
    durationInSeconds === undefined ||
    isNaN(+durationInSeconds) ||
    +durationInSeconds < 0
  ) {
    return "";
  }

  const totalSeconds = Math.round(+durationInSeconds);
  const hours = Math.floor(totalSeconds / SECONDS_IN_HOUR);
  const minutes = Math.floor(
    (totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE,
  );
  const seconds = totalSeconds % SECONDS_IN_MINUTE;
  const secondsPadded = String(seconds).padStart(DURATION_PART_PAD_LENGTH, "0");

  if (!hours) {
    return `${minutes}:${secondsPadded}`;
  }

  const minutesPadded = String(minutes).padStart(DURATION_PART_PAD_LENGTH, "0");
  return `${hours}:${minutesPadded}:${secondsPadded}`;
};

export const isDateInCalendarRange = (
  valueUtc: Dayjs | null,
  calendarDates: DatesUnit | undefined,
): boolean => {
  if (!valueUtc || !calendarDates?.date1 || !calendarDates?.date2) {
    return true;
  }
  const timestamp = valueUtc.valueOf();
  const d1 = dayjs.utc(calendarDates.date1).valueOf();
  const d2 = dayjs.utc(calendarDates.date2).valueOf();
  return timestamp >= d1 && timestamp <= d2;
};
